/**
 * sessionBootstrap.ts
 *
 * Single source of truth for hydrating `useSession` from Supabase Auth.
 *
 * Lifecycle:
 *   1. On app mount: read the persisted session (`supabase.auth.getSession()`).
 *   2. If signed in: resolve EVERY account the user holds an active seat on,
 *      pick the current one, push it all into the Zustand store, mark
 *      status = 'authenticated'.
 *   3. Subscribe to `auth.onAuthStateChange` to react to SIGNED_IN /
 *      SIGNED_OUT / TOKEN_REFRESHED events from elsewhere (other tabs,
 *      password-reset links, expired tokens). A SIGNED_OUT (or a resolved
 *      session with no user) also clears the TanStack Query cache
 *      (`lib/queryClient.ts`) alongside the Zustand store — see that
 *      module's docblock for why a different user signing in next on the
 *      same device needs that, not just this store reset.
 *
 * ## Account model (multi-seat)
 *
 * Access is granted by `account_members`, not by `accounts.owner_user_id`.
 * `current_account_ids()` resolves `auth.uid()` through that table
 * (`accepted_at IS NOT NULL AND disabled_at IS NULL`), so a user can hold seats
 * on several accounts — their own, auto-provisioned at signup, plus any they
 * were invited to. `currentAccountId` is therefore a persisted **selection**,
 * validated on every hydrate (docs/multi-seat-accounts.md §4.4).
 *
 * ## Why the query is rooted at `accounts`
 *
 * The `accounts_member_select` policy is literally
 * `id IN (SELECT account_id FROM current_account_ids())`, so a plain select on
 * `accounts` already returns exactly the accounts with an **active, accepted,
 * non-disabled** seat — enforced by the same function that gates every other
 * account-scoped policy in the schema. Rooting the query here rather than at
 * `account_members` means the client never restates the accepted/disabled
 * predicate and cannot drift from it. `account_members!inner(role)` is embedded
 * only to carry the caller's own role along.
 *
 * It also keeps the account-scoping guard satisfied without an exception:
 * `accounts` carries no `account_id` column, so it is in the guard's
 * `SAFE_TABLES` list, and its own RLS is the only boundary available.
 * (`apps/web/src/lib/__tests__/accountScopingGuard.test.ts`.)
 *
 * Three-shell note: kiosk routes never call `useAuthBootstrap` directly, but
 * that does NOT leave `useSession` unhydrated there. Kiosk entry
 * (`shells/desktop/EnterKioskModal.tsx`) is a plain SPA navigation to
 * `/kiosk` — no reload, no `useSession.clear()`, no `signOut()` — so the
 * in-memory `useSession` store (`lib/store.ts`) keeps whatever authenticated
 * state `useAuthBootstrap` already hydrated before kiosk was entered: real
 * role, real `currentAccountId`, for the whole kiosk session (product
 * decision, vendor#742: the PIN gate on kiosk exit is the intended re-entry
 * control, not session absence). The underlying Supabase auth session is
 * untouched either way, so every `REVOKE ... FROM anon` mitigation in the
 * schema is inert on this path regardless of hydration. The real control
 * keeping staff-only data out of the kiosk shell is the ESLint/Vitest import
 * boundary (`eslint.config.js`'s `kioskShellConfig`), not session state.
 * `useAuthBootstrap` itself is invoked by `components/RequireAuth.tsx` and
 * `features/invite/InviteAcceptPage.tsx` — there is no singular root
 * `<App/>` call site — and gates every non-kiosk route tree that exists
 * today, including the `/m/*` mobile shell pending its retirement
 * (vendor#137).
 */
import { useEffect } from 'react';
import { z } from 'zod';
import { supabase } from './supabase';
import { queryClient } from './queryClient';
import { useSession } from './store';
import {
  type AccountMembership,
  readPersistedAccountId,
  resolveCurrentAccountId,
  writePersistedAccountId,
} from './accountSelection';

/**
 * One row of the membership query, normalised to {@link AccountMembership}.
 *
 * `z.guid()` and not `z.uuid()`: Zod 4's `uuid()` enforces the RFC-4122
 * version and variant nibbles, which the repo's own deterministic fixtures
 * (`00000000-0000-0000-0000-000000000001` in `supabase/seed.sql` and
 * `e2e/helpers/data.ts`) do not set. `uuid()` rejects every seeded account and
 * would break sign-in across local dev and the whole E2E suite. `guid()` is the
 * shape check we actually want here.
 *
 * `account_members` arrives as an ARRAY even though the embed is filtered to
 * one row — PostgREST models `accounts → account_members` as to-many because
 * the FK lives on the child, and `account_members`' primary key
 * `(account_id, user_id)` is what guarantees there is exactly one.
 *
 * `z.tuple([…]).rest(…)` rather than `z.array(…).nonempty()`: both reject an
 * empty embed at runtime, but only the tuple form types the result as
 * `[Member, ...Member[]]`, so reading `[0]` is safe under
 * `noUncheckedIndexedAccess` without an unreachable null branch.
 */
const MemberRoleSchema = z.object({ role: z.enum(['owner', 'operator']) });

const AccountMembershipRowSchema = z
  .object({
    id: z.guid(),
    name: z.string(),
    account_members: z.tuple([MemberRoleSchema]).rest(MemberRoleSchema),
  })
  .transform(
    (row): AccountMembership => ({
      id: row.id,
      name: row.name,
      role: row.account_members[0].role,
    }),
  );

const AccountMembershipListSchema = z.array(AccountMembershipRowSchema);

/**
 * Fetch every account the user holds an active seat on.
 *
 * Returns `[]` on a query error or a payload that fails validation, which
 * routes into the same account-less degraded branch as "genuinely has no
 * seat". That is the safe direction: an unparseable payload must never become
 * a half-populated account list that a `.eq('account_id', …)` is then built
 * from.
 */
async function fetchMemberships(userId: string): Promise<AccountMembership[]> {
  const { data, error } = await supabase
    .from('accounts')
    .select('id, name, account_members!inner(role)')
    .eq('account_members.user_id', userId)
    // Deterministic order: `accounts[0]` is the documented fallback when the
    // persisted selection is stale, so it must not depend on PostgREST's
    // arbitrary row order. `id` breaks ties between same-named accounts.
    .order('name', { ascending: true })
    .order('id', { ascending: true });

  if (error != null) return [];

  const parsed = AccountMembershipListSchema.safeParse(data ?? []);
  return parsed.success ? parsed.data : [];
}

/**
 * The signed-in member's own `display_name`.
 *
 * `public.users` carries `users_self_select USING (id = auth.uid())`, so this
 * reads exactly one row — the caller's own — and needs no `SECURITY DEFINER`
 * directory RPC. `InviteAcceptPage` already does the same read; this is the
 * same query on the sign-in path.
 *
 * **Why this exists:** `session.displayName` was declared, initialised `null`,
 * and then set to `null` here on every sign-in — it was never populated by
 * anything. Every consumer of it (`memberLabel`, and so the dashboard's Split
 * card, the show costs card, the expense sheets) silently fell through to the
 * "self, unnamed" branch and rendered the caller's EMAIL where a name belongs.
 * Reported 2026-08-23 against the Split card.
 *
 * Returns `null` rather than throwing: a missing or unreadable profile row
 * must never block sign-in, and every consumer already degrades to the email.
 */
const SelfDisplayNameRowSchema = z.object({ display_name: z.string().nullable() }).nullable();

async function fetchSelfDisplayName(
  userId: string,
): Promise<{ name: string | null; missing: boolean }> {
  const { data, error } = await supabase
    .from('users')
    .select('display_name')
    .eq('id', userId)
    .maybeSingle();

  if (error != null) return { name: null, missing: false };
  const parsed = SelfDisplayNameRowSchema.safeParse(data);
  // No row, or a shape we do not recognise, is "unknown", not "blank".
  if (!parsed.success || parsed.data === null) return { name: null, missing: false };
  const name = parsed.data.display_name?.trim();
  // `missing` is true only when a profile row was read and its name is blank:
  // that is the one state `RequireAuth` gates on (vendor#1436).
  return name ? { name, missing: false } : { name: null, missing: true };
}

/**
 * Pull the user's accounts from Supabase and push them into the store.
 * Pure async fn — no React.
 *
 * ## What happens to a stale persisted `currentAccountId`
 *
 * It is **discarded and replaced, never scoped by**. The persisted id is only
 * honoured when it appears in the list just returned by the server; otherwise
 * the session falls back to `accounts[0]` (the name-sorted first entry) and the
 * new value is written straight back to storage, so the stale id is not retried
 * on the next load. This covers a revoked seat, a soft-disabled membership, a
 * deleted account, and a different user signing in on the same browser.
 *
 * With no active seat anywhere the store lands in the degraded
 * "authenticated with no account" state (`currentAccountId: null`) and the
 * persisted key is cleared. `RequireSubscription` (`router.tsx`) owns that
 * state and renders `NoAccountFallback`.
 *
 * (This said "`SubscriptionGate` / onboarding" until vendor#159. There was
 * no onboarding: `features/onboarding/` was two storefront-era pages that
 * `router.tsx` never registered, and they are deleted.)
 *
 * **Corrected 2026-08-23 (vendor#1015).** The paragraph above used to end:
 * *"In this repo an account is provisioned by the `handle_new_user` trigger at
 * signup, so the 'authenticated with no account' state is a failure to resolve
 * a seat, not a stage a new user passes through."* **For an invitee it is
 * exactly a stage they pass through.**
 * `20261012091000_no_account_for_invited_users` made the trigger return early —
 * profile row, no account — when the new user is an invitee, so a person who
 * signs up before accepting sits in this state until they do. It also named
 * `SubscriptionGate`, which is a different component in `components/`; the
 * owner of this state is `RequireSubscription`. Both claims produced the dead
 * end vendor#1015 fixed, because they said the screen behind them could not
 * happen.
 *
 * ## Clearing the query cache on a same-tab user switch (vendor#799)
 *
 * `useAuthBootstrap`'s `onAuthStateChange` listener already clears
 * `queryClient` on `SIGNED_OUT`. That leaves one event shape uncovered: a
 * `SIGNED_IN` for a **different** `auth.users` row with no preceding
 * `SIGNED_OUT` — the shared booth-device case where one staff member signs
 * in over another's still-live session. Before this fix this function
 * overwrote `userId` in the store but left every cached query in place, so
 * for up to `staleTime` (`lib/queryClient.ts`, 30s) the new user could still
 * be served the previous user's cached rows — a real disclosure for the
 * Vault's member-private queries specifically (`docs/features/vault.md`
 * §4.2). Comparing against the store's own prior `userId`, here rather than
 * per query key, makes "a different user never reads a stale cache"
 * independent of which auth event fired, so the next member-private surface
 * does not have to remember to key by user too.
 */
export async function hydrateSession(userId: string, email: string): Promise<void> {
  const previousUserId = useSession.getState().userId;
  if (previousUserId != null && previousUserId !== userId) {
    queryClient.clear();
  }

  // Concurrent, not sequential: the name read is independent of the
  // membership read, so it costs no added latency on the sign-in path.
  const [accounts, self] = await Promise.all([
    fetchMemberships(userId),
    fetchSelfDisplayName(userId),
  ]);
  const currentAccountId = resolveCurrentAccountId(accounts, readPersistedAccountId());
  const current = accounts.find((a) => a.id === currentAccountId) ?? null;

  // Write back unconditionally: on the happy path this is a no-op rewrite of
  // the same value, and on the stale path it is what stops the bad id being
  // reconsidered next time.
  writePersistedAccountId(current?.id ?? null);

  useSession.setState({
    status: 'authenticated',
    userId,
    email,
    displayName: self.name,
    displayNameMissing: self.missing,
    accounts,
    currentAccountId: current?.id ?? null,
    shopName: current?.name ?? null,
  });
}

/**
 * React-side bootstrap: call once at the top of any shell that requires auth.
 * Idempotent — subsequent renders re-attach the same listener-once via the
 * effect's cleanup.
 */
export function useAuthBootstrap(): void {
  useEffect(() => {
    let cancelled = false;

    void (async () => {
      const { data } = await supabase.auth.getSession();
      if (cancelled) return;
      const session = data.session;
      if (!session?.user) {
        useSession.getState().clear();
        queryClient.clear();
        return;
      }
      await hydrateSession(session.user.id, session.user.email ?? '');
    })();

    // SIGNED_OUT (or a session that resolved to no user) also clears the
    // TanStack Query cache, not just the Zustand store — a different
    // `auth.users` row may sign in next on the same device (the shared
    // booth-device case). A SIGNED_IN for a different user with no
    // preceding SIGNED_OUT is covered too, inside `hydrateSession` itself
    // (vendor#799) — see that function's docblock. See `lib/queryClient.ts`'s
    // docblock for why this matters most for the Vault's member-private
    // queries specifically.
    const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
      if (cancelled) return;
      if (event === 'SIGNED_OUT' || !session?.user) {
        useSession.getState().clear();
        queryClient.clear();
        return;
      }
      void hydrateSession(session.user.id, session.user.email ?? '');
    });

    return () => {
      cancelled = true;
      sub.subscription.unsubscribe();
    };
  }, []);
}
